/**
 * Ask Lykke about ONE part of a page, right where it is (#deck-machine #103; the Claude Slides pattern Guilherme
 * pointed at): pick an element, a group or an area, write what should change, and only that part changes. The box sits
 * next to the selection with a breadcrumb of what it is about; the message goes through the deck chat with the target.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, MessageSquarePlus, X } from 'lucide-react';


export function AskHere({ anchor, label, busy, onSend, onClose }: {
  /** The part's box on screen (client pixels). */
  anchor: DOMRect;
  /** "Page 6 › Text “Elena · NO”". */
  label: string;
  busy: boolean;
  onSend: (text: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // Below the part, else above it, kept inside the window.
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight, m = 10;
    const left = Math.min(Math.max(m, anchor.left), window.innerWidth - w - m);
    const top = anchor.bottom + m + h <= window.innerHeight ? anchor.bottom + m : Math.max(m, anchor.top - h - m);
    setPos({ left, top });
  }, [anchor]);
  useEffect(() => { setTimeout(() => input.current?.focus(), 0); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    const away = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node) && !text.trim()) onClose(); };
    window.addEventListener('keydown', onKey); window.addEventListener('pointerdown', away, true);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('pointerdown', away, true); };
  }, [onClose, text]);
  const send = (t: string) => { const v = t.trim(); if (!v || busy) return; onSend(v); };
  return createPortal(
    <div ref={ref} data-stage-ui="" role="dialog" aria-label="Ask Lykke about this part"
      style={{ position: 'fixed', left: pos?.left ?? -9999, top: pos?.top ?? -9999, zIndex: 1001, width: 340 }}
      className="rounded-xl border border-[#e7e5e4] bg-white shadow-[0_10px_30px_rgba(16,24,40,0.18)]"
      onPointerDown={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1.5 border-b border-[#f5f5f4] px-3 py-2 text-[11.5px] text-[#78716c]">
        <MessageSquarePlus className="h-3.5 w-3.5 shrink-0 text-[#be43ff]" />
        <span className="min-w-0 flex-1 truncate" title={label}>{label}</span>
        <button onClick={onClose} className="rounded p-0.5 text-[#a8a29e] hover:bg-[#f5f5f4] hover:text-[#44403c]" aria-label="Close"><X className="h-3.5 w-3.5" /></button>
      </div>
      <textarea ref={input} value={text} onChange={(e) => setText(e.target.value)} rows={2} disabled={busy}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(text); } }}
        placeholder={busy ? 'Lykke is working…' : 'What should change here?'} aria-label="What should change here"
        className="block w-full resize-none bg-transparent px-3 pt-2.5 text-[13.5px] leading-relaxed text-[#1c1917] placeholder:text-[#a8a29e] focus:outline-none" />
      <div className="flex justify-end px-2 pb-2">
        <button onClick={() => send(text)} disabled={busy || !text.trim()} className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#be43ff] text-white hover:bg-[#a52fe6] disabled:opacity-40" title="Send to Lykke (Enter)" aria-label="Send to Lykke"><ArrowUp className="h-4 w-4" /></button>
      </div>
    </div>,
    document.body,
  );
}
